import * as React from 'react';
import {
  getSuperAdminStudentHistory as getRosterHistory,
  startSuperAdminRosterHistory as startRosterHistory,
  importSuperAdminRosterHistory as importRosterHistory,
  getSuperAdminRosterTemplate as getRosterTemplate,
  saveSuperAdminHistoryTraits as saveRosterTraits,
  previewSuperAdminRosterImport as previewRosterImport
} from 'Src/helpers/service/superadmin/studentHistory';
import { RosterHistoryStatus, RosterImportPreview } from 'Src/helpers/service/admin/studentHistory';
import { HISTORY_TRAITS, HistoryTraitField, historyCsv } from 'Src/helpers/slt/historyTraits';
import { parseCSVFile } from 'Src/helpers/utils/csvUtils';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import moment from 'moment';
import 'Src/views/GenericView/AdminView/AdminConsole/Terms/AcademicCalendar.scss';
import 'Src/components/Button/Button.css';

const errorText = error =>
  (error && error.response && error.response.data && error.response.data.details && error.response.data.details.text) ||
  'Unable to update roster history. Check the file and dates, then try again.';
function downloadCsv(rows, fields) {
  const text = historyCsv(rows, fields);
  const link = document.createElement('a'),
    url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  link.href = url;
  link.download = 'historical-roster-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}
export function SuperAdminStudentHistory({ schoolId }: { schoolId: string }) {
  const [status, setStatus] = React.useState<RosterHistoryStatus>(null);
  const [error, setError] = React.useState(''),
    [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false),
    [rows, setRows] = React.useState<any[]>([]);
  const [from, setFrom] = React.useState(''),
    [to, setTo] = React.useState(''),
    [reason, setReason] = React.useState('');
  const [confirmed, setConfirmed] = React.useState(false);
  const [blankNoFields, setBlankNoFields] = React.useState<HistoryTraitField[]>([]);
  const [fields, setFields] = React.useState<HistoryTraitField[]>([]);
  const [saved, setSaved] = React.useState('');
  const requestVersion = React.useRef(0);
  const [preview, setPreview] = React.useState<RosterImportPreview>(null);
  const activeSchool = React.useRef(schoolId);
  const uploadVersion = React.useRef(0);
  activeSchool.current = schoolId;

  const applyStatus = (next: RosterHistoryStatus, school = schoolId) => {
    if (activeSchool.current !== school) return;
    setStatus(next);
    setFields(next.traitFields || []);
    setBlankNoFields([]);
    setPreview(null);
  };
  const isCurrent = (school: string, version: number) =>
    activeSchool.current === school && requestVersion.current === version;
  const load = async () => {
    const school = schoolId,
      version = ++requestVersion.current;
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const next = await getRosterHistory(school);
      if (isCurrent(school, version)) applyStatus(next, school);
    } catch (err) {
      if (isCurrent(school, version)) setError(errorText(err));
    } finally {
      if (isCurrent(school, version)) setBusy(false);
    }
  };
  React.useEffect(() => {
    uploadVersion.current++;
    setStatus(null);
    setFields([]);
    setOpen(false);
    setRows([]);
    setPreview(null);
    setFrom('');
    setTo('');
    setReason('');
    setConfirmed(false);
    setBlankNoFields([]);
    setError('');
    setSaved('');
    load();
    return () => {
      requestVersion.current++;
      uploadVersion.current++;
    };
  }, [schoolId]);
  const action = async (fn, message = '') => {
    if (busy) return;
    const school = schoolId,
      version = ++requestVersion.current;
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const next = await fn();
      if (isCurrent(school, version)) {
        applyStatus(next, school);
        setSaved(message);
      }
    } catch (err) {
      if (isCurrent(school, version)) setError(errorText(err));
    } finally {
      if (isCurrent(school, version)) setBusy(false);
    }
  };
  const importFile = async event => {
    const file = event.target.files && event.target.files[0];
    const school = schoolId;
    const version = ++uploadVersion.current;
    if (!file) return;
    setError('');
    setRows([]);
    setConfirmed(false);
    setBlankNoFields([]);
    setPreview(null);
    try {
      const result = await parseCSVFile(file, { header: true, skipEmptyLines: true });
      if (
        result.errors.length ||
        result.data.length > 10000 ||
        !['studentId', 'permissionId', 'from', 'to', 'groupKey', 'groupLabel'].every(
          key => result.meta.fields.indexOf(key) !== -1
        )
      ) {
        throw new Error('CSV headers or rows are invalid.');
      }
      if (activeSchool.current === school && version === uploadVersion.current) setRows(result.data);
    } catch (err) {
      if (activeSchool.current === school && version === uploadVersion.current) {
        setError(
          'Use the CSV template with studentId, permissionId, from, to, groupKey and groupLabel columns (up to 10,000 rows).'
        );
      }
    }
  };
  const submitImport = async event => {
    event.preventDefault();
    if (busy || !status || !confirmed || !rows.length) return;
    setBusy(true);
    setError('');
    const school = schoolId,
      version = ++requestVersion.current;
    const body = { from, to, rows, reason, completeRoster: confirmed, traitRevision: status.traitRevision, blankNoFields };
    try {
      if (!preview) {
        const result = await previewRosterImport(schoolId, body);
        if (isCurrent(school, version)) setPreview(result);
        return;
      }
      const result = await importRosterHistory(schoolId, { ...body, traitRevision: preview.traitRevision });
      if (isCurrent(school, version)) {
        applyStatus(result, school);
        setOpen(false);
      }
    } catch (err) {
      if (isCurrent(school, version)) {
        setPreview(null);
        setError(errorText(err));
      }
    } finally {
      if (isCurrent(school, version)) setBusy(false);
    }
  };
  const settingsDirty =
    fields
      .slice()
      .sort()
      .join() !==
    ((status && status.traitFields) || [])
      .slice()
      .sort()
      .join();
  return (
    <section className="academicCalendar__history mt-3" aria-label="Student history settings">
      <h2>Student history</h2>
      <p>
        Superadmin tools for tracking student characteristics and preparing historical reporting. Schools see the
        resulting insights; these data-management tools are available only here.
      </p>
      {error && (
        <p role="alert" className="alert alert-danger">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="alert alert-success">
          {saved}
        </p>
      )}
      {!status ? (
        <p role="status">{busy ? 'Loading history coverage…' : 'History status is unavailable.'}</p>
      ) : (
        <>
          <p>
            <strong>
              {status.trackingFrom
                ? `Tracking from ${moment(status.trackingFrom).format('D MMM YYYY HH:mm')}`
                : 'Automatic tracking has not started'}
            </strong>
          </p>
          <fieldset className="academicCalendar__traitSettings" disabled={busy || open || status.pendingWrites > 0}>
            <legend>Student characteristics to track</legend>
            <p>
              Choose only the information this school maintains. Updates from MIS and student records will keep its
              history automatically.
            </p>
            <div className="academicCalendar__traitOptions">
              {HISTORY_TRAITS.map(trait => (
                <label key={trait.key}>
                  <input
                    type="checkbox"
                    checked={fields.includes(trait.key)}
                    onChange={event => {
                      setFields(
                        event.target.checked ? [...fields, trait.key] : fields.filter(field => field !== trait.key)
                      );
                      setPreview(null);
                      setSaved('');
                    }}
                  />
                  <span>
                    <strong>{trait.label}</strong>
                    <small>{trait.name}</small>
                  </span>
                </label>
              ))}
            </div>
            <p>
              Saving selected fields starts history if it is not running. Earlier periods require an archive. Older
              records without source evidence remain Unknown. Blank PP/SEN/FSM marks mean No only when the source
              supplies that field completely. Missing source fields and blank Ethnicity remain Unknown.
            </p>
            {
              <button
                type="button"
                className="sit-btn sit-btn--primary"
                disabled={busy || !settingsDirty}
                onClick={() => {
                  if (busy || !settingsDirty || status.pendingWrites > 0) return;
                  return action(
                    () => saveRosterTraits(schoolId, fields, status.traitRevision || 0),
                    'History settings saved.'
                  );
                }}
              >
                Save tracking settings
              </button>
            }
            {!!(status.traitSettings || []).length && (
              <p className="academicCalendar__traitUpdated">
                Last changed{' '}
                {moment(status.traitSettings[status.traitSettings.length - 1].changedAt).format('D MMM YYYY HH:mm')}
              </p>
            )}
          </fieldset>
          {(status.interruptedAt || status.pendingWrites > 0) && (
            <p className="alert alert-warning">
              History contains unresolved or concurrent membership writes. Affected periods cannot be presented as
              complete. Finish any updates, then rebuild the baseline with a reason.
            </p>
          )}
          <div className="academicCalendar__toolbar">
            {!status.trackingFrom && (
              <button
                disabled={busy || settingsDirty || status.pendingWrites > 0}
                className="sit-btn sit-btn--primary"
                onClick={() => action(() => startRosterHistory(schoolId))}
              >
                Start history tracking
              </button>
            )}
            {
              <button
                disabled={busy || settingsDirty}
                className="sit-btn sit-btn--secondary"
                onClick={() => {
                  setRows([]);
                  setReason('');
                  setConfirmed(false);
                  setPreview(null);
                  setError('');
                  setOpen(true);
                }}
              >
                Import historical roster
              </button>
            }
          </div>
          {settingsDirty && <p>Save the tracking settings before starting history or preparing an archive import.</p>}
          {(status.interruptedAt || status.pendingWrites > 0) && (
            <div>
              <label>
                Reason for rebuilding the baseline
                <input className="termsInput" value={reason} onChange={event => setReason(event.target.value)} />
              </label>
              <button
                disabled={busy || !reason.trim()}
                className="sit-btn sit-btn--secondary"
                onClick={() => action(() => startRosterHistory(schoolId, { reset: true, reason }))}
              >
                Rebuild current baseline
              </button>
              <p>Earlier verified coverage is preserved; gaps are not silently filled with today's roster.</p>
            </div>
          )}
          <p className="academicCalendar__context">
            The CSV import accepts reviewed enrolment intervals. Monthly MIS snapshots use the dedicated archive import
            tool.
          </p>
          <details>
            <summary>Verified historical coverage ({status.ranges.length} intervals)</summary>
            <ul>
              {status.ranges.map((range, index) => (
                <li key={index}>
                  {moment(range.from).format('D MMM YYYY')} –{' '}
                  {moment(range.to)
                    .subtract(1, 'millisecond')
                    .format('D MMM YYYY')}{' '}
                  · {range.source} · recorded {moment(range.recordedAt).format('D MMM YYYY HH:mm')}
                </li>
              ))}
            </ul>
          </details>
          <details>
            <summary>
              Monthly archive observations (
              {(status.archiveImports || []).reduce((sum, batch) => sum + batch.snapshots.length, 0)} snapshots)
            </summary>
            <p>
              These are point-in-time rosters. They do not establish continuous membership between observation dates.
            </p>
            {(status.archiveImports || []).map(batch => (
              <div key={batch.batchId}>
                <strong>Imported {moment(batch.importedAt).format('D MMM YYYY HH:mm')}</strong>
                <ul>
                  {batch.snapshots.map(snapshot => (
                    <li key={snapshot.observedAt}>
                      {moment(snapshot.observedAt)
                        .utc()
                        .format('D MMM YYYY HH:mm')}{' '}
                      UTC · {snapshot.students} students
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </details>
        </>
      )}
      <button className="sit-btn sit-btn--secondary" disabled={busy || open} onClick={load}>
        Refresh status
      </button>
      <SimpleModal
        isOpen={open}
        title="Import a complete historical roster"
        onCloseClick={() => {
          if (!busy) setOpen(false);
        }}
        showFooter={false}
      >
        <form className="academicCalendar__form" onSubmit={submitImport}>
          <fieldset className="academicCalendar__importFields" disabled={busy}>
            <p>
              Include every student enrolled at any time in the coverage interval, including leavers. Use separate,
              non-overlapping rows when a year group or tracked characteristic changes. Row dates are inclusive and use
              YYYY-MM-DD.
            </p>
            <p>
              For PP, SEN and FSM use Yes, No or Unknown. Confirm complete source columns below to treat their blank
              marks as No; other blanks remain Unknown. Ethnicity uses the code supplied by your archive.
              Characteristics not enabled for this school cannot be imported.
            </p>
            <p>
              The template supplies existing student and permission IDs. Fill dates and groups from an authoritative
              archive; current group assignments are deliberately left blank.
            </p>
            {error && (
              <p role="alert" className="alert alert-danger">
                {error}
              </p>
            )}
            <button
              type="button"
              className="sit-btn sit-btn--secondary"
              onClick={() => {
                const school = schoolId,
                  version = requestVersion.current;
                setError('');
                getRosterTemplate(school)
                  .then(rows => {
                    if (isCurrent(school, version)) downloadCsv(rows, status.traitFields || []);
                  })
                  .catch(err => {
                    if (isCurrent(school, version)) setError(errorText(err));
                  });
              }}
            >
              Download roster template
            </button>
            <DatePicker
              label="Coverage from"
              format="DD/MM/YYYY"
              value={from ? moment(from, 'YYYY-MM-DD').toDate() : null}
              onChange={value => {
                setFrom(value ? moment(value as Date).format('YYYY-MM-DD') : '');
                setPreview(null);
              }}
              required
            />
            <DatePicker
              label="Coverage to"
              format="DD/MM/YYYY"
              value={to ? moment(to, 'YYYY-MM-DD').toDate() : null}
              onChange={value => {
                setTo(value ? moment(value as Date).format('YYYY-MM-DD') : '');
                setPreview(null);
              }}
              required
            />
            <label>
              Archive source and reason
              <input
                className="termsInput"
                required
                maxLength={500}
                value={reason}
                onChange={event => {
                  setReason(event.target.value);
                  setPreview(null);
                }}
                placeholder="e.g. MIS enrolment export for 2025/2026, reviewed by…"
              />
            </label>
            <label>
              Historical roster CSV
              <input type="file" accept=".csv,text/csv" onChange={importFile} />
            </label>
            <p>
              {rows.length} rows ready for validation. Names are not imported into the historical membership records.
            </p>
            {rows.length > 0 && (
              <fieldset>
                <legend>Complete source columns: blank means No</legend>
                <p>Confirm only columns fully supplied by this archive. Use Unknown for any unavailable value.</p>
                {HISTORY_TRAITS.filter(trait => trait.key !== 'ethnicityCode' && status.traitFields.includes(trait.key)).map(trait => (
                  <label key={trait.key}>
                    <input
                      type="checkbox"
                      checked={blankNoFields.includes(trait.key)}
                      onChange={event => {
                        setBlankNoFields(event.target.checked ? [...blankNoFields, trait.key] : blankNoFields.filter(field => field !== trait.key));
                        setPreview(null);
                      }}
                    />{' '}{trait.label}
                  </label>
                ))}
              </fieldset>
            )}
            {rows.length > 0 && (
              <div className="slt-tableWrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Student ID</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Year group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        <td>{row.studentId}</td>
                        <td>{row.from}</td>
                        <td>{row.to}</td>
                        <td>{row.groupLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <label>
              <span>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={event => {
                    setConfirmed(event.target.checked);
                    setPreview(null);
                  }}
                />{' '}
                I confirm this is the complete school roster for the coverage interval and the group assignments have
                been checked.
              </span>
            </label>
            {preview && (
              <div className="alert alert-info" role="status">
                <strong>
                  Validated: {preview.students} students · {preview.rows} intervals
                </strong>
                <p>Review the coverage before importing. Unknown values will stay unknown.</p>
                <ul>
                  {preview.traits.map(trait => (
                    <li key={trait.field}>
                      {(HISTORY_TRAITS.find(item => item.key === trait.field) || { label: trait.field }).label}:{' '}
                      {trait.knownRows} intervals with data · {trait.unknownRows} unknown
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="submit" className="sit-btn sit-btn--primary" disabled={busy || !confirmed || !rows.length}>
              {busy ? 'Processing…' : preview ? 'Import validated roster' : 'Validate archive'}
            </button>
          </fieldset>
        </form>
      </SimpleModal>
    </section>
  );
}
